/**
 * @fileoverview Syndicator Showdown — Single source of truth for showdown
 * lifecycle/status/routing/progress
 * @module utils/showdownStatus
 *
 * Pure, dependency-free helpers shared by:
 *   - the logged-in /showdowns page (ShowdownsPage.jsx)
 *   - the public landing page (via ShowdownCard.jsx's computePublicStatus,
 *     which delegates its lifecycle determination to getShowdownDisplayState)
 *   - the leaderboard default-selection helper (utils/leaderboardSelection.js,
 *     which still consumes getRaceProgress/isFinalisedSlate/toMs directly)
 *
 * `getShowdownDisplayState` is the ONE place that turns raw game_slates
 * fields into a lifecycle + tab bucket + button state — every consumer above
 * must read status through it rather than re-deriving its own interpretation
 * of `gameOpenTime`/`gameCloseTime`/`finalised`/`pricesLocked`.
 */

export function toMs(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return isNaN(ms) ? null : ms;
}

/**
 * Whether a game_slates doc has been operator-finalised. Centralised here
 * since several places (lifecycle bucket, Board button, leaderboard default
 * selection) need to agree on exactly the same check.
 */
export function isFinalisedSlate(slate) {
  return !!slate && (
    slate.finalised === true ||
    slate.status === 'finalised' ||
    slate.status === 'finalized' ||
    slate.status === 'completed' ||
    slate.isFinal === true
  );
}

/**
 * Race progress for a showdown card, derived from the denormalized
 * `completedRaces`/`totalRaces` counters on the game_slates doc itself, OR
 * from a fresher `races` array when the caller already has one (races
 * subcollection docs, each with a `status` field). `resultedRaces` is
 * clamped to `totalRaces` defensively (counters are incremented
 * independently and could theoretically drift).
 */
export function getRaceProgress(slate, races = null) {
  const totalRaces = Math.max(0, slate?.totalRaces || (Array.isArray(races) ? races.length : 0) || 0);
  const rawResulted = Array.isArray(races) && races.length > 0
    ? races.filter(r => r?.status === 'resulted').length
    : Math.max(0, slate?.completedRaces || 0);
  const resultedRaces = totalRaces > 0 ? Math.min(rawResulted, totalRaces) : rawResulted;
  const progressPct = totalRaces > 0 ? Math.round((resultedRaces / totalRaces) * 100) : 0;
  return {
    totalRaces,
    resultedRaces,
    progressPct,
    hasAnyResult: resultedRaces > 0,
    isComplete: totalRaces > 0 && resultedRaces >= totalRaces,
  };
}

function formatDuration(ms) {
  if (!ms || ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d}d ${rh}h`;
  }
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  const s = Math.floor((ms % 60000) / 1000);
  return `${s}s`;
}

const LIFECYCLE_STATUS = {
  archived:       { label: 'ARCHIVED',        tone: 'muted'     },
  completed:      { label: 'FINALISED',       tone: 'completed' },
  resultsPending: { label: 'RESULTS PENDING', tone: 'pending'   },
  live:           { label: 'LIVE NOW',        tone: 'live'      },
  entriesOpen:    { label: 'ENTRIES OPEN',    tone: 'live'      },
  openingSoon:    { label: 'OPENING SOON',    tone: 'soon'      },
};

/**
 * The single source of truth for a showdown's display state — lifecycle,
 * tab bucket, button routing, race progress, and status copy, all derived
 * from the SAME underlying game_slates fields the (working) public landing
 * page and admin dashboard already use: `gameOpenTime`, `gameCloseTime`,
 * `pricesLocked`, `finalised`/`status`, `archived`, `completedRaces`/
 * `totalRaces`, plus the early-entry-window fields `entriesOpen`/
 * `entriesOpenAt` where set.
 *
 * Lifecycle stages (in the order a showdown normally passes through them):
 *   'openingSoon'    - nothing open yet
 *   'entriesOpen'    - admin opened entries early, before gameOpenTime
 *   'live'           - within the [gameOpenTime, gameCloseTime) window
 *   'resultsPending' - past gameCloseTime, not yet finalised by an operator
 *   'completed'      - finalised
 *   'archived'       - operator archived the slate (never shown anywhere)
 *
 * `isEnterable` is intentionally independent of lifecycle: a "results
 * pending" game with a still-open per-race trading window (pricesLocked
 * still true, not finalised) must remain enterable, exactly like before.
 *
 * @param {Object} slate - game_slates doc (with `id`)
 * @param {Array|null} [races] - optional races subcollection docs for a
 *   fresher resultedRaceCount than the denormalized completedRaces counter
 * @param {*} [userEntry] - truthy if the user has an entry: an entry object,
 *   a non-empty array of entries, or an entry count > 0
 * @returns {Object|null} display state, or null if `slate` is falsy
 */
export function getShowdownDisplayState(slate, races = null, userEntry = null) {
  if (!slate) return null;

  const now = Date.now();
  const openMs  = toMs(slate.gameOpenTime);
  const closeMs = toMs(slate.gameCloseTime);
  const entriesOpenMs = toMs(slate.entriesOpenAt);

  const archived  = slate.archived === true;
  const completed = isFinalisedSlate(slate);
  const pastClose = !!closeMs && now >= closeMs;
  const adminForcedOpen = slate.status === 'live' || slate.status === 'entries_open';
  const pastOpen  = adminForcedOpen || (!!openMs && now >= openMs);
  const entriesOpenedEarly =
    !pastOpen && (slate.entriesOpen === true || (entriesOpenMs != null && now >= entriesOpenMs));
  const hasUserEntry = Array.isArray(userEntry) ? userEntry.length > 0 : !!userEntry;

  // Archiving a finalised game (AdminSlatesPage.jsx "Archive" action) is only
  // meant to hide it from admin/discovery surfaces — its own confirm copy
  // promises "players can still see their entries and results". A finalised
  // + archived slate the user actually entered must therefore still resolve
  // to the 'completed' lifecycle (rank/score/leaderboard link intact); one
  // with NO entry from this user stays fully hidden as 'archived' (filtered
  // out upstream by buildShowdownItems).
  let lifecycle;
  if (completed && (!archived || hasUserEntry)) lifecycle = 'completed';
  else if (archived) lifecycle = 'archived';
  else if (pastClose) lifecycle = 'resultsPending';
  else if (pastOpen) lifecycle = 'live';
  else if (entriesOpenedEarly) lifecycle = 'entriesOpen';
  else lifecycle = 'openingSoon';

  const isOpeningSoon   = lifecycle === 'openingSoon';
  const isInProgress    = lifecycle === 'live' || lifecycle === 'entriesOpen';
  const isResultsPending = lifecycle === 'resultsPending';
  const isCompleted     = lifecycle === 'completed';

  // A game is enterable if prices are locked and it hasn't been finalised —
  // deliberately independent of the close-time-derived lifecycle stage, so a
  // per-race trading window kept open past gameCloseTime (results pending,
  // pricesLocked still true, not finalised) stays enterable rather than
  // silently routing to the leaderboard.
  const isEnterable = !archived && !completed && slate.pricesLocked === true;

  const { totalRaces, resultedRaces, progressPct, hasAnyResult } = getRaceProgress(slate, races);

  const tabBucket = lifecycle === 'archived'
    ? null
    : isCompleted
      ? 'completed'
      : isOpeningSoon
        ? 'upcoming'
        : 'live'; // entriesOpen | live | resultsPending

  const boardEnabled = hasAnyResult || isCompleted;

  // ── Primary + secondary (Board) action ──────────────────────────────────
  let primaryActionLabel;
  let primaryActionRoute = null;
  let primaryActionDisabled = false;

  if (isCompleted) {
    primaryActionLabel = 'View Results';
    primaryActionRoute = `/leaderboard/${slate.id}`;
  } else if (isEnterable) {
    primaryActionLabel = hasUserEntry ? 'Continue' : 'Play Now';
    primaryActionRoute = `/slate/${slate.id}`;
  } else if (isOpeningSoon || (lifecycle === 'entriesOpen' && !isEnterable)) {
    primaryActionLabel = 'Opens Soon';
    primaryActionDisabled = true;
  } else if (hasAnyResult) {
    // In progress / results pending, entries locked, at least one result in.
    primaryActionLabel = 'View Progress';
    primaryActionRoute = `/leaderboard/${slate.id}`;
  } else {
    // In progress, entries locked, nothing resulted yet.
    primaryActionLabel = 'In Progress';
    primaryActionDisabled = true;
  }

  const secondaryActionLabel = 'Board';
  const secondaryActionRoute = boardEnabled ? `/leaderboard/${slate.id}` : null;
  const secondaryActionDisabled = !boardEnabled;
  const secondaryActionHelperText = boardEnabled ? null : 'Leaderboard opens after Race 1 results.';

  // ── Countdown + status copy ──────────────────────────────────────────────
  let countdownLabel = null;
  if (isOpeningSoon && openMs) countdownLabel = `Opens in ${formatDuration(openMs - now)}`;
  else if (lifecycle === 'entriesOpen' && openMs) countdownLabel = `Live in ${formatDuration(openMs - now)}`;
  else if (lifecycle === 'live' && closeMs) countdownLabel = `Closes in ${formatDuration(closeMs - now)}`;

  const { label: statusLabel, tone: statusTone } = LIFECYCLE_STATUS[lifecycle] || LIFECYCLE_STATUS.openingSoon;

  return {
    lifecycle,
    tabBucket,
    isEnterable,
    isOpeningSoon,
    isInProgress,
    isResultsPending,
    isCompleted,
    hasUserEntry,
    hasAnyResult,
    resultedRaceCount: resultedRaces,
    totalRaceCount: totalRaces,
    progressPct,
    boardEnabled,
    primaryActionLabel,
    primaryActionRoute,
    primaryActionDisabled,
    secondaryActionLabel,
    secondaryActionRoute,
    secondaryActionDisabled,
    secondaryActionHelperText,
    countdownLabel,
    statusLabel,
    statusTone,
  };
}

/**
 * Builds the { slate, state } pairs `filterShowdownsForTab` operates on.
 * `entryCountBySlate` / `racesBySlate` are both optional lookup maps keyed
 * by slateId — omit `racesBySlate` to fall back to the denormalized
 * completedRaces/totalRaces counters.
 */
export function buildShowdownItems(slates, entryCountBySlate = {}, racesBySlate = {}) {
  return (slates || [])
    // Archived slates stay hidden everywhere, EXCEPT a finalised one the
    // current user actually entered — see the archived/hasUserEntry note in
    // getShowdownDisplayState above (AdminSlatesPage.jsx "Archive" promises
    // players can still see their own entries/results after archiving). An
    // archived slate that never got finalised (e.g. an abandoned test game)
    // stays fully hidden even if the user has an entry in it.
    .filter(s => s && (s.archived !== true || (isFinalisedSlate(s) && !!entryCountBySlate?.[s.id])))
    .map(slate => ({
      slate,
      state: getShowdownDisplayState(slate, racesBySlate?.[slate.id] || null, entryCountBySlate?.[slate.id]),
    }));
}

const inLiveTab = ({ state }) =>
  state.tabBucket === 'live' && (state.hasUserEntry || !state.isResultsPending);
const inUpcomingTab = ({ state }) => state.tabBucket === 'upcoming';
const inCompletedTab = ({ state }) => state.hasUserEntry && state.tabBucket === 'completed';
const inMyEntriesTab = ({ state }) => state.hasUserEntry;
const inAllTab = (item) => inLiveTab(item) || inUpcomingTab(item) || inCompletedTab(item);

const TAB_BUCKET_PRIORITY = { live: 0, upcoming: 1, completed: 2 };

/**
 * Filters + sorts `{ slate, state }` items (see buildShowdownItems) for a
 * given Showdowns page tab. The logged-in /showdowns page is BOTH a
 * discovery surface (publicly live/opening-soon games, whether or not the
 * user has entered) AND a "my showdowns" tracker (`entered`/`completed`
 * are always scoped to the user's own entries) — see "P0 Showdowns Display"
 * fix notes.
 *
 *   'live'      - public live/entries-open showdowns, PLUS any entered
 *                 in-progress showdown (including "results pending" ones
 *                 entries have closed on, e.g. Flemington).
 *   'upcoming'  - public opening-soon showdowns (entered or not).
 *   'entered'   - every showdown the user has a real entry in, any bucket.
 *   'completed' - finalised showdowns the user entered (never a public
 *                 archive of every finalised game system-wide).
 *   'all'       - live ∪ upcoming ∪ (entered ∩ completed) — the full
 *                 "available to play or already entered" set, never every
 *                 archived/generated game.
 *
 * Returns the filtered/sorted `{ slate, state }` items (not just slates) so
 * callers can render badges/progress/actions straight from `state` without
 * recomputing it.
 *
 * @param {Array<{slate:Object,state:Object}>} items
 * @param {string} filter - 'all' | 'live' | 'upcoming' | 'entered' | 'completed'
 */
export function filterShowdownsForTab(items, filter) {
  let list = items || [];

  if (filter === 'live') list = list.filter(inLiveTab);
  else if (filter === 'upcoming') list = list.filter(inUpcomingTab);
  else if (filter === 'entered') list = list.filter(inMyEntriesTab);
  else if (filter === 'completed') list = list.filter(inCompletedTab);
  else list = list.filter(inAllTab); // 'all' (or anything else)

  return [...list].sort((a, b) => {
    const pa = TAB_BUCKET_PRIORITY[a.state.tabBucket] ?? 9;
    const pb = TAB_BUCKET_PRIORITY[b.state.tabBucket] ?? 9;
    if (pa !== pb) return pa - pb;
    return (toMs(b.slate.gameCloseTime) || toMs(b.slate.gameOpenTime) || 0)
         - (toMs(a.slate.gameCloseTime) || toMs(a.slate.gameOpenTime) || 0);
  });
}
